// banking/src/pages/onboarding/corporate/components/SOCSelector.tsx

import React, { useState, useEffect, useRef } from 'react';
import { getTranslation, type Language } from '../corporate-translations';

interface SOCSelectorProps {
  value: string;
  onChange: (code: string) => void;
  language: Language;
  error?: string;
  label?: string;
  required?: boolean;
}

interface SOCCode {
  code: string;
  description: string;
}

const SOCSelector: React.FC<SOCSelectorProps> = ({
  value,
  onChange,
  language,
  error,
  label,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [socCodes, setSocCodes] = useState<SOCCode[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load SOC codes
  useEffect(() => {
    const loadSOCCodes = async () => {
      setLoading(true);
      try {
        // Common SOC codes for corporate representatives
        const commonCodes: SOCCode[] = [
          // Management Occupations
          { code: '111011', description: 'Chief Executives' },
          { code: '111021', description: 'General and Operations Managers' },
          { code: '112021', description: 'Marketing Managers' },
          { code: '112022', description: 'Sales Managers' },
          { code: '113021', description: 'Computer and Information Systems Managers' },
          { code: '113031', description: 'Financial Managers' },
          { code: '113051', description: 'Industrial Production Managers' },
          { code: '113061', description: 'Purchasing Managers' },
          { code: '113071', description: 'Transportation, Storage, and Distribution Managers' },
          { code: '113121', description: 'Human Resources Managers' },
          { code: '119021', description: 'Construction Managers' },
          { code: '119041', description: 'Architectural and Engineering Managers' },
          { code: '119051', description: 'Food Service Managers' },
          { code: '119111', description: 'Medical and Health Services Managers' },
          { code: '119151', description: 'Social and Community Service Managers' },
          { code: '119199', description: 'Managers, All Other' },

          // Business and Financial Operations
          { code: '131111', description: 'Management Analysts' },
          { code: '131141', description: 'Compensation, Benefits, and Job Analysis Specialists' },
          { code: '131151', description: 'Training and Development Specialists' },
          { code: '131161', description: 'Market Research Analysts and Marketing Specialists' },
          { code: '131199', description: 'Business Operations Specialists, All Other' },
          { code: '132011', description: 'Accountants and Auditors' },
          { code: '132031', description: 'Budget Analysts' },
          { code: '132041', description: 'Credit Analysts' },
          { code: '132051', description: 'Financial and Investment Analysts' },
          { code: '132052', description: 'Personal Financial Advisors' },
          { code: '132053', description: 'Insurance Underwriters' },
          { code: '132072', description: 'Loan Officers' },
          { code: '132081', description: 'Tax Examiners and Collectors, and Revenue Agents' },
          { code: '132082', description: 'Tax Preparers' },

          // Computer and Mathematical
          { code: '151211', description: 'Computer Systems Analysts' },
          { code: '151212', description: 'Information Security Analysts' },
          { code: '151221', description: 'Computer and Information Research Scientists' },
          { code: '151231', description: 'Computer Network Support Specialists' },
          { code: '151232', description: 'Computer User Support Specialists' },
          { code: '151241', description: 'Computer Network Architects' },
          { code: '151243', description: 'Database Administrators' },
          { code: '151244', description: 'Network and Computer Systems Administrators' },
          { code: '151251', description: 'Computer Programmers' },
          { code: '151252', description: 'Software Developers' },
          { code: '151253', description: 'Software Quality Assurance Analysts and Testers' },
          { code: '151254', description: 'Web Developers' },
          { code: '151255', description: 'Web and Digital Interface Designers' },
          { code: '151299', description: 'Computer Occupations, All Other' },

          // Architecture and Engineering
          { code: '171012', description: 'Landscape Architects' },
          { code: '172011', description: 'Aerospace Engineers' },
          { code: '172051', description: 'Civil Engineers' },
          { code: '172061', description: 'Computer Hardware Engineers' },
          { code: '172071', description: 'Electrical Engineers' },
          { code: '172072', description: 'Electronics Engineers, Except Computer' },
          { code: '172112', description: 'Industrial Engineers' },
          { code: '172141', description: 'Mechanical Engineers' },

          // Legal
          { code: '231011', description: 'Lawyers' },
          { code: '231012', description: 'Judicial Law Clerks' },
          { code: '231023', description: 'Judges, Magistrate Judges, and Magistrates' },
          { code: '232011', description: 'Paralegals and Legal Assistants' },

          // Sales
          { code: '411011', description: 'First-Line Supervisors of Retail Sales Workers' },
          { code: '411012', description: 'First-Line Supervisors of Non-Retail Sales Workers' },
          { code: '413031', description: 'Securities, Commodities, and Financial Services Sales Agents' },
          { code: '413099', description: 'Sales Representatives, Services, All Other' },

          // Office and Administrative Support
          { code: '431011', description: 'First-Line Supervisors of Office and Administrative Support Workers' },
          { code: '433031', description: 'Bookkeeping, Accounting, and Auditing Clerks' },
          { code: '434051', description: 'Customer Service Representatives' },
          { code: '436011', description: 'Executive Secretaries and Executive Administrative Assistants' },
          { code: '436013', description: 'Medical Secretaries and Administrative Assistants' },
          { code: '436014', description: 'Secretaries and Administrative Assistants, Except Legal, Medical, and Executive' },

          // Special codes
          { code: 'STUDENT', description: 'Student' },
          { code: 'RETIRED', description: 'Retired' },
          { code: 'UNEMPLOYED', description: 'Unemployed' },
        ];

        setSocCodes(commonCodes);
      } catch (error) {
        console.error('Error loading SOC codes:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSOCCodes();
  }, []);

  const filteredCodes = socCodes.filter((item) =>
    item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCode = socCodes.find((c) => c.code === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={`w-full px-4 py-2 text-left border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
          error ? 'border-red-500' : 'border-gray-300'
        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {selectedCode ? (
          <span>{selectedCode.code} - {selectedCode.description}</span>
        ) : (
          <span className="text-gray-400">
            {loading ? getTranslation('loading', language) : getTranslation('occupation_placeholder', language)}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={getTranslation('search_placeholder', language)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B3A369]"
              autoFocus
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filteredCodes.length > 0 ? (
              filteredCodes.map((code) => (
                <button
                  key={code.code}
                  type="button"
                  onClick={() => {
                    onChange(code.code);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-[#F5F3E8] transition-colors ${
                    code.code === value ? 'bg-blue-100 font-medium' : ''
                  }`}
                >
                  <div className="font-medium text-sm text-[#B3A369]">{code.code}</div>
                  <div className="text-sm text-gray-700">{code.description}</div>
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                {getTranslation('no_results', language)}
              </div>
            )}
          </div>
        </div>
      )}

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default SOCSelector;
